import { cn } from "@/lib/utils";

// Exact OptiDev mark geometry (from optidev-icon.svg), recolored theme-aware.
/** Cloud outline (outer + inner contour, even-odd → smooth outline band). */
const CLOUD_PATH =
  "M262.87,221.36c9.39-.06,19.73-1.73,29.6-5.23,25.87-9.16,42.26-32.46,41.75-59.34-.51-27.44-17.77-52.15-41.98-60.12-4.31-1.42-5.24-3.14-5.83-7.57-2.89-21.91-13.73-36.3-32.24-44.92-13.85-6.45-32.88-7.93-48-2.23l-.24-.37c-.54-.83-.99-1.53-1.43-2.21C189,14.71,162.69,0,134.13,0c-12.19,0-24.02,2.7-35.13,8.02-28.61,13.71-44.99,37.36-48.68,70.32-.35,3.06-1.14,4.12-3.84,5.12C13.45,95.66-5.15,128.73,1.25,163.91c2.88,15.82,11.61,30.45,24.57,41.19,12.98,10.75,29.21,16.15,45.5,16.17l63.27.19s115.38,0,128.27-.1ZM134.1,210.66h-11.99v-.02h-14.15c-11.84,0-23.67,0-35.51-.01-30.07-.01-56.07-21.33-60.49-49.59-4.95-31.7,11.86-59.43,41.83-69,4.95-1.58,6.94-4.26,7.07-9.53.82-32.18,24.15-61.27,55.47-69.16,5.85-1.47,11.85-2.22,17.82-2.22,25.8,0,49.94,14.12,62.99,36.85,1.64,2.85,3.33,5.8,7.09,5.8,1.2,0,2.51-.3,4.01-.94,6.1-2.57,12.41-3.87,18.77-3.87,22.17,0,45.74,19.36,48.3,39.17,1.49,11.52,5.35,16.36,15.88,19.9,19.12,6.44,32.94,28.69,32.13,51.74-.77,21.57-15.37,40.39-36.35,46.83-7.75,2.38-16.19,3.69-24.41,3.81-11.47.16-27.83.25-43.5.26v-.04h-84.95Z";
/** Left chevron `<`. */
const CODE_LEFT =
  "M124.85,169.34l-44.75-26.8c-2.18-1.37-3.27-3.23-3.27-5.58,0-2.1,1.09-3.92,3.27-5.46l44.75-26.92c2.18-1.13,4.28-1.15,6.31-.06,2.02,1.09,3.03,2.81,3.03,5.15s-1.01,4.24-3.03,5.46l-40.87,24.26v-5.22l40.87,24.5c2.02,1.29,3.03,3.07,3.03,5.34,0,1.62-.51,2.97-1.52,4.06-1.01,1.09-2.24,1.78-3.7,2.06-1.45.28-2.83.02-4.12-.79Z";
/** Right chevron `>`. */
const CODE_RIGHT =
  "M209.38,169.34c-1.29.81-2.67,1.07-4.12.79-1.45-.28-2.69-.97-3.7-2.06-1.01-1.09-1.52-2.44-1.52-4.06,0-2.26,1.01-4.04,3.03-5.34l40.87-24.5v5.22l-40.87-24.26c-2.02-1.21-3.03-3.03-3.03-5.46s1.01-4.06,3.03-5.15c2.02-1.09,4.12-1.07,6.31.06l44.75,26.92c2.18,1.54,3.27,3.36,3.27,5.46,0,2.35-1.09,4.21-3.27,5.58l-44.75,26.8Z";
/** Center slash `/`. */
const CODE_SLASH =
  "M151.51,173.62c-1.03,0-2.08-.24-3.07-.75-3.26-1.7-4.53-5.72-2.83-8.98l31.2-59.98c1.7-3.26,5.72-4.53,8.98-2.83,3.26,1.7,4.53,5.72,2.83,8.98l-31.2,59.98c-1.19,2.28-3.51,3.59-5.91,3.59Z";

/**
 * The agent's "thinking" indicator — the exact OptiDev mark (cloud + `</>`)
 * with a bright highlight sweeping through it via an animated gradient, next to
 * a softly shimmering label. Smooth + unique to OptiDev, theme-aware (no
 * spinner, bulb, sparkle, or robot). Used for the first wait (before the first
 * token) and in-flight placeholders.
 */
export function ThinkingIndicator({
  label = "Thinking",
  className,
}: {
  label?: string;
  className?: string;
}) {
  return (
    <span className={cn("inline-flex items-center gap-2", className)}>
      <svg
        viewBox="0 0 334.22 221.46"
        className="h-[16px] w-[24px] shrink-0"
        aria-hidden
      >
        <defs>
          {/* ~160-wide highlight band translating across the mark; outside the
              band it pads to muted, so a bright spot sweeps over a muted mark. */}
          <linearGradient
            id="thinking-cloud-grad"
            gradientUnits="userSpaceOnUse"
            x1="0"
            y1="0"
            x2="160"
            y2="0"
          >
            <stop offset="0" stopColor="hsl(var(--muted-foreground))" />
            <stop offset="0.5" stopColor="hsl(var(--foreground))" />
            <stop offset="1" stopColor="hsl(var(--muted-foreground))" />
            <animateTransform
              attributeName="gradientTransform"
              type="translate"
              from="-200 0"
              to="334 0"
              dur="1.8s"
              repeatCount="indefinite"
            />
          </linearGradient>
        </defs>

        <path d={CLOUD_PATH} fill="url(#thinking-cloud-grad)" fillRule="evenodd" />
        <path d={CODE_LEFT} fill="url(#thinking-cloud-grad)" />
        <path d={CODE_RIGHT} fill="url(#thinking-cloud-grad)" />
        <path d={CODE_SLASH} fill="url(#thinking-cloud-grad)" />
      </svg>
      <span className="text-shimmer select-none text-sm font-medium tracking-tight">
        {label}
      </span>
    </span>
  );
}
